import MCPLogo from "@/assets/MCPLogo.svg";
import { Button } from "@/components/ui";
import { useHandler } from "@/hooks";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { getMcpServersQueryOptions } from "@/lib/hooks/useMcpServers";
import { getTasksQueryOptions } from "@/lib/hooks/useTasks";
import { cn } from "@/lib/utils";
import { usePanelStore } from "@/state/stores";
import { useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Edit3, X } from "lucide-react";
import { memo } from "react";
import { McpServersTab, SuggestionsTab, TasksTab } from "./tabs";

export const DrawerTabs = memo(function DrawerTabs() {
  const { setActiveTab, activeTab, isPanelOpen } = usePanelStore();
  const projectId = useActiveProjectId();

  return (
    <div className="flex gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("suggestions")}
        disabled={!projectId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "suggestions" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <Edit3 className="h-4 w-4" /> Suggestions
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("mcp-servers")}
        disabled={!projectId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "mcp-servers" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <MCPLogo className="h-3.5 w-3.5" />
        MCP Servers
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("tasks")}
        disabled={!projectId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "tasks" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <CheckSquare className="h-3.5 w-3.5" />
        Tasks
      </Button>
    </div>
  );
});

export function Drawer() {
  const queryClient = useQueryClient();
  const activeProjectId = useActiveProjectId();
  const isPanelOpen = usePanelStore(({ isPanelOpen }) => isPanelOpen);
  const activeTab = usePanelStore(({ activeTab }) => activeTab);

  const prefetchAllTabs = useHandler(() => {
    if (!activeProjectId) {
      return;
    }
    queryClient.prefetchQuery(getTasksQueryOptions(activeProjectId));
    queryClient.prefetchQuery(getMcpServersQueryOptions(activeProjectId));
  });

  if (!isPanelOpen) {
    return null;
  }

  return (
    <div
      key={activeTab}
      onMouseEnter={prefetchAllTabs}
      className={cn(
        "animate-slide-up",
        activeTab !== "suggestions" &&
          "border border-b-0 px-3 pt-2 rounded-t-xl pb-6 -mb-5 bg-background/50",
      )}
    >
      <DrawerContent />
    </div>
  );
}

function DrawerContent() {
  const activeTab = usePanelStore(({ activeTab }) => activeTab);
  const closePanel = usePanelStore(({ closePanel }) => closePanel);

  const Title = () => {
    switch (activeTab) {
      case "mcp-servers":
        return "MCP Servers";
      case "suggestions":
        return "Suggestions";
      case "tasks":
        return "Tasks";
    }
  };

  const Content = () => {
    switch (activeTab) {
      case "suggestions": {
        return <SuggestionsTab />;
      }
      case "mcp-servers": {
        return <McpServersTab />;
      }
      case "tasks": {
        return <TasksTab />;
      }
    }
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs text-muted-foreground">
          <Title />
        </h3>

        <Button
          variant="ghost"
          size="icon-xs"
          onClick={closePanel}
          className="text-muted-foreground hover:text-foreground -mr-1"
          aria-label="Close"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      <Content />
    </div>
  );
}
